import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Table from 'react-bootstrap/Table'
import Spinner from 'react-bootstrap/Spinner'
import Select, { components } from 'react-select'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faTimes, faUsers } from '@fortawesome/free-solid-svg-icons'
import { AppBadge } from 'shared/badges'
import MutedText from 'shared/forms/muted-text'
import useUserOptions from 'api/hooks/use-user-options'
import useEmployeesRoles from 'api/hooks/use-employees-roles'

const StatusBadge = ({ status }) =>
  status === 'active' ? (
    <AppBadge variant="success" pill>
      Active
    </AppBadge>
  ) : (
    <AppBadge variant="secondary" pill>
      Inactive
    </AppBadge>
  )

StatusBadge.propTypes = { status: PropTypes.string }

// react-select only searches the label (external id) by default — also match
// against the user's name and department.
const filterUserOption = (option, search) => {
  if (!search) return true
  const { external_id: externalId, full_name: fullName, department_name: departmentName } = option.data
  return [externalId, fullName, departmentName].some((field) =>
    (field || '').toLowerCase().includes(search.toLowerCase())
  )
}

// Renders each user option with the active/inactive badge so reviewers can see a
// user's status before adding them to the comparison.
const UserOption = (props) => (
  <components.Option {...props}>
    <div className="d-flex justify-content-between align-items-center">
      <span>
        <span className="fw-semibold">{props.data.external_id}</span>
        {props.data.full_name && (
          <span className="text-muted ms-2">{props.data.full_name}</span>
        )}
        {props.data.department_name && (
          <span className="text-muted ms-2 small">
            {props.data.department_name}
          </span>
        )}
      </span>
      <StatusBadge status={props.data.status} />
    </div>
  </components.Option>
)

function CompareUsersModal({ show, setShow }) {
  const [selected, setSelected] = useState([])
  // Controlled search text so it survives selections — react-select clears the
  // input on select, which would break picking several people from one
  // department search.
  const [searchText, setSearchText] = useState('')

  // Only load the employee list once the modal is opened.
  const { isSuccess, data: userOptionsData } = useUserOptions({
    options: { enabled: show }
  })

  const employees = useMemo(() => (isSuccess ? userOptionsData.data : []), [
    isSuccess,
    userOptionsData
  ])

  const selectedIds = useMemo(() => selected.map((e) => e.id), [selected])

  const roleResults = useEmployeesRoles(selectedIds)

  // Options grouped by department, "No department" last.
  const userOptions = useMemo(() => {
    const groups = new Map()
    employees
      .filter((e) => !selectedIds.includes(e.id))
      .forEach((e) => {
        const department = e.department_name || 'No department'
        if (!groups.has(department)) groups.set(department, [])
        // value/label are what react-select keys on; the spread keeps the
        // employee fields the comparison table renders.
        groups.get(department).push({ value: e.id, label: e.external_id, ...e })
      })
    return Array.from(groups.keys())
      .sort((a, b) => {
        if (a === 'No department') return 1
        if (b === 'No department') return -1
        return a.localeCompare(b)
      })
      .map((label) => ({ label, options: groups.get(label) }))
  }, [employees, selectedIds])

  // The option carries every employee field the table renders, so it can be
  // stored directly. Selected users are filtered out of userOptions, but guard
  // against double-fire anyway.
  const handleAddUser = (option) => {
    if (!option) return
    setSelected((prev) =>
      prev.some((e) => e.id === option.id) ? prev : [...prev, option]
    )
  }

  const handleRemove = (id) => {
    setSelected((prev) => prev.filter((e) => e.id !== id))
  }

  // The component stays mounted while hidden — discard the comparison on
  // close so reopening starts fresh.
  const handleClose = () => {
    setShow(false)
    setSelected([])
    setSearchText('')
  }

  // Union of roles across every selected user. A role only enters the grid when
  // at least one of the selected users actually has it.
  const roles = useMemo(() => {
    const map = new Map()
    roleResults.forEach((res) => {
      ;(res.data?.roles || []).forEach((role) => {
        if (!map.has(role.id)) {
          map.set(role.id, { id: role.id, name: role.name, caption: role.caption })
        }
      })
    })
    return Array.from(map.values()).sort((a, b) =>
      (a.name || '').localeCompare(b.name || '')
    )
  }, [roleResults])

  // Per-user lookup of the role ids they hold, keyed by employee id so the
  // table never depends on `selected` and `roleResults` staying index-aligned.
  const roleIdsByUser = useMemo(() => {
    const map = new Map()
    selectedIds.forEach((id, index) => {
      map.set(
        id,
        new Set((roleResults[index]?.data?.roles || []).map((role) => role.id))
      )
    })
    return map
  }, [selectedIds, roleResults])

  const isLoadingRoles =
    selected.length > 0 && roleResults.some((res) => res.isLoading)

  return (
    <Modal show={show} onHide={handleClose} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="lead fw-normal">
          <FontAwesomeIcon icon={faUsers} className="text-muted mx-2" />
          Compare Users
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ minHeight: '60vh' }}>
        <div className="row g-3 mb-4 align-items-end">
          <div className="col-md-6">
            <label className="form-label small text-muted">Add a user</label>
            <Select
              options={userOptions}
              value={null}
              placeholder="Select a user..."
              onChange={handleAddUser}
              components={{ Option: UserOption }}
              filterOption={filterUserOption}
              // zIndex must beat the sticky .table-responsive thead (z-index: 10).
              styles={{
                menu: (base) => ({ ...base, marginTop: 0, zIndex: 20 })
              }}
              isClearable={false}
              closeMenuOnSelect={false}
              blurInputOnSelect={false}
              inputValue={searchText}
              onInputChange={(value, { action }) => {
                if (action === 'input-change') setSearchText(value)
              }}
              onMenuClose={() => setSearchText('')}
            />
          </div>
          {selected.length > 0 && (
            <div className="col-auto">
              <Button
                variant="light"
                className="text-danger"
                onClick={() => setSelected([])}
              >
                <FontAwesomeIcon icon={faTimes} className="me-2" />
                Remove all users
              </Button>
            </div>
          )}
        </div>

        {selected.length === 0 ? (
          <MutedText className="d-block text-center py-5">
            Add users above to start comparing roles.
          </MutedText>
        ) : (
          <Table
            responsive
            bordered
            size="sm"
            className="bg-white align-middle"
          >
            <thead className="table-light">
              <tr>
                <th className="pt-1" style={{ minWidth: '220px' }}>
                  Role
                </th>
                {selected.map((employee) => (
                  <th
                    key={`compare-user-${employee.id}`}
                    className="text-center pt-1"
                  >
                    <div className="d-flex flex-column align-items-center">
                      <span className="d-flex align-items-center">
                        {employee.external_id}
                        <Button
                          variant="link"
                          size="sm"
                          className="text-muted hover-text-danger p-0 ms-2"
                          title="Remove user"
                          onClick={() => handleRemove(employee.id)}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </Button>
                      </span>
                      {employee.department_name && (
                        <MutedText className="small">
                          {employee.department_name}
                        </MutedText>
                      )}
                      <StatusBadge status={employee.status} />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoadingRoles && (
                <tr>
                  <td colSpan={selected.length + 1} className="text-center py-4">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading roles...
                  </td>
                </tr>
              )}
              {!isLoadingRoles && roles.length === 0 && (
                <tr>
                  <td colSpan={selected.length + 1} className="text-center py-4">
                    <MutedText>
                      None of the selected users have any roles.
                    </MutedText>
                  </td>
                </tr>
              )}
              {!isLoadingRoles &&
                roles.map((role) => (
                  <tr key={`compare-role-${role.id}`}>
                    <td>
                      <a
                        href={`/roles/${role.id}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {role.name}
                      </a>
                      {role.caption && (
                        <MutedText as="div">{role.caption}</MutedText>
                      )}
                    </td>
                    {selected.map((employee) => (
                      <td
                        key={`compare-cell-${role.id}-${employee.id}`}
                        className="text-center"
                      >
                        {roleIdsByUser.get(employee.id)?.has(role.id) ? (
                          <FontAwesomeIcon
                            icon={faCheck}
                            className="text-success"
                          />
                        ) : null}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="light"
          className="bg-white text-muted"
          onClick={handleClose}
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

CompareUsersModal.propTypes = {
  show: PropTypes.bool.isRequired,
  setShow: PropTypes.func.isRequired
}

export default CompareUsersModal
